import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface RhetoricDevice {
  name: string;
  nameEn: string;
  icon: string;
  definition: string;
  example: string;
  highlight: string;
  effect: string;
  color: string;
}

const DEVICES: RhetoricDevice[] = [
  {
    name: '比喻',
    nameEn: 'Metaphor',
    icon: '🌊',
    definition: '用一事物形容另一事物，使表达生动形象',
    example: '"生活是一场没有排练的戏剧"',
    highlight: '生活 → 戏剧',
    effect: '化抽象为具象，增强感染力',
    color: '#4ECDC4',
  },
  {
    name: '拟人',
    nameEn: 'Personification',
    icon: '🌸',
    definition: '赋予非人事物以人的特征和行为',
    example: '"春风轻轻地抚摸着大地"',
    highlight: '春风 → 抚摸',
    effect: '赋予生命力，营造亲切氛围',
    color: '#FF6B9D',
  },
  {
    name: '排比',
    nameEn: 'Parallelism',
    icon: '📐',
    definition: '连用三个或以上结构相似的句式',
    example: '"人生自古谁无死？留取丹心照汗青"',
    highlight: '结构相似 × 3+',
    effect: '增强节奏感和气势',
    color: '#FF8C5A',
  },
  {
    name: '夸张',
    nameEn: 'Hyperbole',
    icon: '🔥',
    definition: '故意放大或缩小事物特征以强调效果',
    example: '"白发三千丈，缘愁似个长"',
    highlight: '三千丈 ← 极度放大',
    effect: '突出事物本质，加深印象',
    color: '#FFD700',
  },
  {
    name: '对偶',
    nameEn: 'Antithesis',
    icon: '☯️',
    definition: '用字数相等、结构对称的语句表达',
    example: '"山重水复疑无路，柳暗花明又一村"',
    highlight: '山重水复 ⟷ 柳暗花明',
    effect: '形式工整，音韵和谐',
    color: '#A78BFA',
  },
];

const InkDot: React.FC<{ x: number; y: number; size: number; opacity: number; color: string }> = ({
  x,
  y,
  size,
  opacity,
  color,
}) => (
  <div
    style={{
      position: 'absolute',
      left: x,
      top: y,
      width: size,
      height: size,
      borderRadius: '50%',
      background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
      opacity,
      filter: 'blur(2px)',
    }}
  />
);

export const LiteraryRhetoricAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const currentIndex = Math.min(
    Math.floor(Math.max(0, frame - 55) / 95),
    DEVICES.length - 1,
  );

  const inkDots = React.useMemo(() => {
    const out: { x: number; y: number; s: number; b: number }[] = [];
    for (let i = 0; i < 25; i++) {
      const h1 = ((i * 7919 + 104729) % 100000) / 100000;
      const h2 = ((i * 6271 + 73849) % 100000) / 100000;
      out.push({ x: h1 * 1920, y: h2 * 1080, s: 20 + h1 * 60, b: 0.03 + h2 * 0.06 });
    }
    return out;
  }, []);

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0F0F1A 0%, #1A1025 50%, #0F0F1A 100%)',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      {inkDots.map((d, i) => (
        <InkDot
          key={i}
          x={d.x}
          y={d.y}
          size={d.s}
          opacity={d.b + Math.sin(frame * 0.03 + i) * 0.02}
          color={DEVICES[i % DEVICES.length].color}
        />
      ))}

      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 60,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-25, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 72,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #4ECDC4, #A78BFA)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          文学修辞手法
        </h1>
        <p style={{ fontSize: 28, margin: '8px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Rhetorical Devices · 语言的艺术
        </p>
      </div>

      {DEVICES.map((device, i) => {
        const phaseStart = 55 + i * 95;
        const phaseEnd = phaseStart + 90;
        const fadeIn = interpolate(frame, [phaseStart, phaseStart + 18], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const fadeOut = interpolate(frame, [phaseEnd - 12, phaseEnd], [1, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const opacity = i === DEVICES.length - 1 ? fadeIn : fadeIn * fadeOut;

        if (frame < phaseStart || opacity <= 0) return null;

        const quoteReveal = interpolate(frame, [phaseStart + 20, phaseStart + 50], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        return (
          <div
            key={device.nameEn}
            style={{
              position: 'absolute',
              top: 280,
              left: 60,
              right: 60,
              opacity,
            }}
          >
            <div style={{ display: 'flex', gap: 50 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 28 }}>
                  <span style={{ fontSize: 56 }}>{device.icon}</span>
                  <div>
                    <h2 style={{ fontSize: 52, fontWeight: 700, color: device.color, margin: 0 }}>
                      {device.name}
                    </h2>
                    <span style={{ fontSize: 30, color: 'rgba(255,255,255,0.35)' }}>
                      {device.nameEn}
                    </span>
                  </div>
                </div>

                <p style={{ fontSize: 30, color: '#D1D5DB', lineHeight: 1.7, margin: '0 0 28px' }}>
                  {device.definition}
                </p>

                <div
                  style={{
                    padding: '28px 34px',
                    background: `${device.color}10`,
                    border: `2px solid ${device.color}35`,
                    borderRadius: 18,
                  }}
                >
                  <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>
                    经典示例
                  </div>
                  <p
                    style={{
                      fontSize: 34,
                      color: '#fff',
                      fontStyle: 'italic',
                      margin: 0,
                      lineHeight: 1.5,
                      opacity: quoteReveal,
                    }}
                  >
                    {device.example}
                  </p>
                </div>
              </div>

              <div style={{ width: 480 }}>
                <div
                  style={{
                    padding: '32px 36px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 20,
                    marginTop: 42,
                    marginBottom: 26,
                  }}
                >
                  <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)', marginBottom: 12 }}>
                    核心映射
                  </div>
                  <div style={{ fontSize: 38, fontWeight: 700, color: device.color }}>
                    {device.highlight}
                  </div>
                </div>

                <div
                  style={{
                    padding: '24px 30px',
                    background: `linear-gradient(135deg, ${device.color}12, transparent)`,
                    borderRadius: 16,
                    borderLeft: `4px solid ${device.color}`,
                  }}
                >
                  <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                    修辞效果
                  </div>
                  <p style={{ fontSize: 28, color: '#E5E7EB', margin: 0, lineHeight: 1.6 }}>
                    {device.effect}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 14,
        }}
      >
        {DEVICES.map((d, i) => (
          <div
            key={d.nameEn}
            style={{
              width: i === currentIndex ? 44 : 12,
              height: 12,
              borderRadius: 6,
              background: i === currentIndex ? d.color : 'rgba(255,255,255,0.15)',
            }}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};
